import { useEffect, useState } from 'react'
import apiClient from '../../api/apiClient'
import { AxiosError } from 'axios'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { RichTextEditor } from './RichTextEditor'
import { ImageUpload } from './ImageUpload'
import './RichTextEditor.css'

type FeedbackCategory = '기능추가' | '버그제보' | '화면스타일' | '기타'
type FeedbackStatus = '대기' | '진행' | '완료' | '반려'

interface FeedbackImage {
  id: number
  originalFilename: string
  imageUrl: string
  fileSize: number
}

interface FeedbackComment {
  id: number
  content: string
  authorId: number
  authorName: string
  createdAt: string | number[]
}

interface Feedback {
  id: number
  title: string
  content: string
  category: FeedbackCategory
  status: FeedbackStatus
  authorId: number
  authorName: string
  authorUsername: string
  images: FeedbackImage[]
  comments: FeedbackComment[]
  commentCount: number
  createdAt: string | number[]
  updatedAt: string | number[]
  canEdit: boolean
  canDelete: boolean
}

interface FeedbackFormData {
  title: string
  content: string
  category: FeedbackCategory
}

const categories: FeedbackCategory[] = ['기능추가', '버그제보', '화면스타일', '기타']
const statuses: (FeedbackStatus | 'all')[] = ['all', '대기', '진행', '완료', '반려']
const statusLabels: Record<FeedbackStatus | 'all', string> = {
  all: '전체',
  대기: '대기',
  진행: '진행중',
  완료: '완료',
  반려: '반려',
}

export function FeedbackHistory() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState<FeedbackStatus | 'all'>('all')
  const [selectedCategory, setSelectedCategory] = useState<FeedbackCategory | 'all'>('all')

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [formData, setFormData] = useState<FeedbackFormData>({
    title: '',
    content: '',
    category: '기능추가',
  })
  const [formImages, setFormImages] = useState<FeedbackImage[]>([])

  // Detail modal state
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null)
  const [newComment, setNewComment] = useState('')

  // User info
  const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
  const isAdmin = userInfo.role === 'ADMIN'

  useEffect(() => {
    fetchFeedbacks()
  }, [selectedStatus, selectedCategory])

  const fetchFeedbacks = async () => {
    try {
      setLoading(true)
      let url = '/api/feedbacks'

      if (selectedStatus !== 'all') {
        url = `/api/feedbacks/status/${selectedStatus}`
      } else if (selectedCategory !== 'all') {
        url = `/api/feedbacks/category/${selectedCategory}`
      }

      const response = await apiClient.get(url)
      setFeedbacks(response.data || [])
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error)
      setFeedbacks([])
    } finally {
      setLoading(false)
    }
  }

  const fetchFeedbackDetail = async (id: number) => {
    try {
      const response = await apiClient.get(`/api/feedbacks/${id}`)
      setSelectedFeedback(response.data)
    } catch (error) {
      console.error('Failed to fetch feedback detail:', error)
    }
  }

  const handleCreateFeedback = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await apiClient.post('/api/feedbacks', formData)
      const newFeedback = response.data

      // 이미지가 있으면 업로드
      for (const image of formImages) {
        if (image.id < 0 && image.imageUrl.startsWith('data:')) {
          // 로컬 프리뷰 이미지를 실제 파일로 변환하여 업로드
          const blob = await fetch(image.imageUrl).then(r => r.blob())
          const file = new File([blob], image.originalFilename, { type: blob.type })
          const formData = new FormData()
          formData.append('file', file)
          await apiClient.post(`/api/feedbacks/${newFeedback.id}/images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }
      }

      setIsFormOpen(false)
      setFormData({ title: '', content: '', category: '기능추가' })
      setFormImages([])
      fetchFeedbacks()
    } catch (error) {
      console.error('Failed to create feedback:', error)
      if (error instanceof AxiosError) {
        alert(error.response?.data?.message || '피드백 생성에 실패했습니다.')
      }
    }
  }

  const handleDeleteFeedback = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return
    try {
      await apiClient.delete(`/api/feedbacks/${id}`)
      setSelectedFeedback(null)
      fetchFeedbacks()
    } catch (error) {
      console.error('Failed to delete feedback:', error)
      if (error instanceof AxiosError) {
        alert(error.response?.data?.message || '삭제에 실패했습니다.')
      }
    }
  }

  const handleStatusChange = async (id: number, status: FeedbackStatus) => {
    try {
      const response = await apiClient.put(`/api/feedbacks/${id}/status`, { status })
      setSelectedFeedback(response.data)
      fetchFeedbacks()
    } catch (error) {
      console.error('Failed to update status:', error)
      if (error instanceof AxiosError) {
        alert(error.response?.data?.message || '상태 변경에 실패했습니다.')
      }
    }
  }

  const handleAddComment = async () => {
    if (!selectedFeedback || !newComment.trim()) return
    try {
      await apiClient.post(`/api/feedbacks/${selectedFeedback.id}/comments`, { content: newComment })
      setNewComment('')
      fetchFeedbackDetail(selectedFeedback.id)
    } catch (error) {
      console.error('Failed to add comment:', error)
      if (error instanceof AxiosError) {
        alert(error.response?.data?.message || '댓글 작성에 실패했습니다.')
      }
    }
  }

  const formatDateTime = (dateTime: string | number[] | null) => {
    if (!dateTime) return 'N/A'
    let date: Date
    if (Array.isArray(dateTime)) {
      const [year, month, day, hour = 0, minute = 0] = dateTime
      date = new Date(year, month - 1, day, hour, minute)
    } else {
      date = new Date(dateTime)
    }
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: FeedbackStatus) => {
    const colors: Record<FeedbackStatus, string> = {
      대기: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      진행: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      완료: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      반려: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    }
    return colors[status]
  }

  const getCategoryColor = (category: FeedbackCategory) => {
    const colors: Record<FeedbackCategory, string> = {
      기능추가: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      버그제보: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      화면스타일: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      기타: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    }
    return colors[category]
  }

  // Form View
  if (isFormOpen) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">새 요청사항</h2>
            <form onSubmit={handleCreateFeedback}>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">카테고리</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setFormData({ ...formData, category: cat })}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                          formData.category === cat
                            ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">제목</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="요청사항 제목을 입력하세요"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">내용</label>
                  <RichTextEditor
                    content={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="상세 내용을 입력하세요..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">이미지 첨부</label>
                  <ImageUpload
                    feedbackId={null}
                    images={formImages}
                    onImagesChange={setFormImages}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" variant="primary">
                    등록하기
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => setIsFormOpen(false)}>
                    취소
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </Card>
      </div>
    )
  }

  // Detail Modal View
  if (selectedFeedback) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="p-6">
            <Button onClick={() => setSelectedFeedback(null)} className="mb-4">
              ← 목록으로
            </Button>

            <div className="flex flex-wrap gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedFeedback.status)}`}>
                {selectedFeedback.status}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(selectedFeedback.category)}`}>
                {selectedFeedback.category}
              </span>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{selectedFeedback.title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {selectedFeedback.authorName} · {formatDateTime(selectedFeedback.createdAt)}
            </p>

            <div className="feedback-content mb-6" dangerouslySetInnerHTML={{ __html: selectedFeedback.content }} />

            {selectedFeedback.images && selectedFeedback.images.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">첨부 이미지</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {selectedFeedback.images.map((image) => (
                    <a key={image.id} href={image.imageUrl} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={image.imageUrl} alt={image.originalFilename} className="w-full h-24 object-cover rounded-lg hover:opacity-75 transition-opacity" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Admin Controls */}
            {isAdmin && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">상태 변경</h3>
                <div className="flex flex-wrap gap-2">
                  {(['대기', '진행', '완료', '반려'] as FeedbackStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(selectedFeedback.id, status)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedFeedback.status === status
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                관리자 답변 ({selectedFeedback.comments?.length || 0})
              </h3>

              {selectedFeedback.comments && selectedFeedback.comments.length > 0 && (
                <div className="space-y-4 mb-6">
                  {selectedFeedback.comments.map((comment) => (
                    <div key={comment.id} className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-gray-800 dark:text-gray-200">{comment.content}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {comment.authorName} · {formatDateTime(comment.createdAt)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {isAdmin && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="답변을 입력하세요..."
                    className="flex-1 px-4 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <Button onClick={handleAddComment}>답변</Button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {(selectedFeedback.canDelete) && (
              <div className="flex gap-2 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={() => handleDeleteFeedback(selectedFeedback.id)}>
                  삭제
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    )
  }

  // List View
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">요청사항</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            기능 추가, 버그 제보, 화면 스타일 변경 등 요청사항을 등록해주세요.
          </p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="inline-flex items-center px-4 py-2.5 bg-gray-700 dark:bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          새 요청사항
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
        {/* Status Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex overflow-x-auto">
            {statuses.map((status) => (
              <button
                key={status}
                onClick={() => {
                  setSelectedStatus(status)
                  setSelectedCategory('all')
                }}
                className={`
                  flex-1 min-w-[100px] px-6 py-4 text-sm font-medium transition-all duration-200
                  ${selectedStatus === status
                    ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white bg-gray-100 dark:bg-gray-700'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }
                `}
              >
                <div className="flex items-center justify-center gap-2">
                  {status === '대기' && (
                    <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                  )}
                  {status === '진행' && (
                    <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                  )}
                  {status === '완료' && (
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  )}
                  {status === '반려' && (
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                  {statusLabels[status]}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Category Pills */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setSelectedCategory('all')
                setSelectedStatus('all')
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedCategory === 'all'
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
              }`}
            >
              전체
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category)
                  setSelectedStatus('all')
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feedback List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">로딩 중...</p>
          </div>
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg">등록된 요청사항이 없습니다.</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">새 요청사항을 등록해보세요.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {feedbacks.map((feedback) => (
            <div
              key={feedback.id}
              onClick={() => fetchFeedbackDetail(feedback.id)}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-200 cursor-pointer group overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                      feedback.status === '대기' ? 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700' :
                      feedback.status === '진행' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700' :
                      feedback.status === '완료' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-500 border-green-200 dark:border-green-700' :
                      'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-500 border-red-200 dark:border-red-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        feedback.status === '대기' ? 'bg-gray-500' :
                        feedback.status === '진행' ? 'bg-yellow-500 animate-pulse' :
                        feedback.status === '완료' ? 'bg-green-500' :
                        'bg-red-500'
                      }`}></span>
                      {feedback.status}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(feedback.category).replace('bg-', 'bg-').replace('text-', 'text-').replace('100', '50').replace('800', '700')} border-${getCategoryColor(feedback.category).split(' ')[0].replace('bg-', '').replace('100', '200')}`}>
                      {feedback.category}
                    </span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-gray-700 dark:group-hover:text-white">
                  {feedback.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {feedback.authorName}
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                  {feedback.content?.replace(/<[^>]*>/g, '')}
                </p>

                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{formatDateTime(feedback.createdAt)}</span>
                  </div>
                  {feedback.commentCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span>답변 {feedback.commentCount}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
